import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MediaPlayerHelperService } from './media-player-helper.service';
import { SafeResourceUrlService } from '../../../services/safe-resource-url.service';
import { AnnotationPluginEventService } from './annotation-plugin-event.service';
import { EmitterService } from '../../../services/emitter.service';
import { MalwareScanningStatus, SimpleFilePages } from '../../../interfaces';
import { onVideoPlayerDisposed } from '../../../services/annotation.service';
import { SafeStyle } from '@angular/platform-browser';
import { MediaPlayerFile } from '../../../interfaces/media-player-store.interface';
import { FileTranscodeService } from './file-transcode.service';
import { AuthService } from '../../../services/auth.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../services/media-player-store.service';
import { FileTypeService } from '../../../services/file-type.service';
import { FileUtilsService } from '../../../services/file-utils.service';
import { MediaType, FileTranscodingStatus } from '../../../models/media-player-types';

@Injectable()
export class AssetUploadService implements OnDestroy {
  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  private readonly helperService: MediaPlayerHelperService = inject(MediaPlayerHelperService);
  private readonly safeResourceUrlService: SafeResourceUrlService = inject(SafeResourceUrlService);
  private readonly annotationPluginService: AnnotationPluginEventService = inject(AnnotationPluginEventService);
  private readonly fileTranscodeService: FileTranscodeService = inject(FileTranscodeService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly gaService: GoogleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private metadataCheckTimeout: ReturnType<typeof setTimeout> | null = null;
  private instanceId: string | null = null;
  private readonly fileTypeService: FileTypeService = inject(FileTypeService);
  private readonly fileUtilsService: FileUtilsService = inject(FileUtilsService);
  ngOnDestroy(): void {
    if (this.instanceId) {
      this.disposePlayerIfExist(this.instanceId);
      this.disposeAllPlugins(this.instanceId);
    }
    this.cancelMetadataCheck();
  }

  /**
   * Common logic for resetting and reinitializing media player
   */
  public resetAndReinitialize(
    item: MediaPlayerFile | null,
    enablePage: boolean | undefined,
    filePages: SimpleFilePages | null | undefined,
    clearAnnotationsLoaded: boolean,
    instanceId: string
  ): void {
    if (item?.malwareScanningStatus !== MalwareScanningStatus.Clean) {
      return;
    }

    this.instanceId = instanceId;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    this.updateContainerSize(item);
    this.cancelMetadataCheck();
    store.updateStore({ isImagePluginReady: false });
    store.updateStore({ isAudioPluginReady: false });
    store.updateStore({ isVideoPluginReady: false });
    this.disposePlayerIfExist(instanceId);
    this.disposeAllPlugins(instanceId);
    store.updateStore({ mediaType: null });
    store.updateStore({ isError: false });
    if (clearAnnotationsLoaded) {
      store.updateStore({ annotationsLoaded: false });
    }
    if (store.videoTypeCheckIntervalSignal()) {
      clearTimeout(store.videoTypeCheckIntervalSignal());
      store.updateStore({ videoTypeCheckInterval: null });
    }
    if (store.videoSourceCheckIntervalSignal()) {
      clearTimeout(store.videoSourceCheckIntervalSignal());
      store.updateStore({ videoSourceCheckInterval: null });
    }

    setTimeout(() => {
      store.updateStore({ annotations: [] });
      this.init(item, true, enablePage, filePages, instanceId);
    }, 0);
  }

  private updateContainerClasses(type: string, containers: HTMLElement[]): void {
    const allClasses: string[] = ['video-selected', 'audio-selected', 'image-selected', 'pdf-selected'];
    const targetClass: string = `${type}-selected`;

    containers.forEach((element: HTMLElement | null) => {
      if (element) {
        allClasses.forEach((cls: string) => element.classList.remove(cls));
        element.classList.add(targetClass);
      }
    });
  }

  private updateContainerSize(item: MediaPlayerFile | null): void {
    if (!item) {
      return;
    }

    const type: string = this.helperService.getMediaType(item);
    const previewContainer: HTMLElement | null = document.getElementById('previewContainer');
    const assetContainer: HTMLElement | null = document.getElementById('assetContainer');

    if (!previewContainer || !assetContainer) {
      return;
    }

    const typeClass: string = ['pdf', 'officeDoc', 'powerpoint', 'keynote'].includes(type) ? 'pdf' : type;
    this.updateContainerClasses(typeClass, [previewContainer, assetContainer]);
  }

  public init(
    item: MediaPlayerFile | null,
    forceInit?: boolean,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    if (!instanceId) return;
    this.instanceId = instanceId;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    store.updateStore({ isFileNotSupported: false });

    // Handle uploading state – re-run preview logic when upload completes
    if (item?.status === 'uploading') {
      this.handleUploadingItem(item, enablePage, filePages, instanceId);
      return;
    }

    if (!item || !forceInit) {
      return;
    }

    const ext: string = (item.name || '').split('.').pop() || '';
    store.updateStore({ item });
    this.initializeByMediaType(item, ext, enablePage, filePages, instanceId);
  }

  private handleUploadingItem(
    item: MediaPlayerFile,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    EmitterService.get('file-upload-done-' + item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (instanceId) {
          this.resetAndReinitialize(item, enablePage, filePages, false, instanceId);
        }
      });
  }

  private initializeByMediaType(
    item: MediaPlayerFile,
    ext: string,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    if (!instanceId) return;
    if (this.fileTypeService.isVideoFile(item)) {
      this.handleVideoItem(instanceId);
    } else if (this.fileTypeService.isAudioFile(item)) {
      this.handleAudioItem(instanceId);
    } else if (
      this.fileTypeService.isImageFile(item) &&
      this.fileTypeService.isSupportedImageFormat(item?.contentType)
    ) {
      this.handleImageItem(item, instanceId);
    } else if (item?.contentType === 'application/pdf') {
      this.handlePdfItem(item, ext, enablePage, filePages, instanceId);
    } else if (this.fileTypeService.isOfficeDocWithoutPpt(item)) {
      this.handleOfficeDocItem(item, enablePage, filePages, instanceId);
    } else if (this.fileTypeService.isPowerPointFile(item)) {
      this.handlePowerPointItem(item, enablePage, filePages, instanceId);
    } else if (this.fileTypeService.isConvertibleImageFormat(item)) {
      this.handleConvertibleImageItem(item, enablePage, filePages, instanceId);
    } else if (item.name.endsWith('.key')) {
      this.handleKeynoteItem(item, enablePage, filePages, instanceId);
    } else {
      this.handleUnsupportedItem(item, instanceId);
    }
  }

  private handleVideoItem(instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;
    store.updateStore({ mediaType: MediaType.VIDEO, isVideoSourceSet: false });
    this.triggerVideoViewerAction('checkValidOriginal', instanceId);
  }

  private handleAudioItem(instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;
    store.updateStore({ mediaType: MediaType.AUDIO });
    this.triggerAudioViewerAction('prepareWaveSurfer', instanceId);
    this.annotationPluginService.initializeAudioAnnotations(instanceId);
  }

  private handleImageItem(item: MediaPlayerFile, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;
    store.updateStore({ imgSrc: this.safeResourceUrlService.toSafeStyle('url("' + item.signedURL + '")') });
    this.annotationPluginService.initializeImageAnnotations(instanceId, false);
  }

  private handlePdfItem(
    item: MediaPlayerFile,
    ext: string,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    if (!instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const pdfExtensionsToSkip: string[] = ['ai'];
    if (pdfExtensionsToSkip.includes(ext)) {
      this.handleUnsupportedItem(item, instanceId);
      return;
    }

    // Update item and store with previewSRC
    if (item.signedURL) {
      item.previewSRC = item.signedURL;
      store.updateStore({
        item: { ...item, previewSRC: item.signedURL },
      });
    }
    this.annotationPluginService.initializeComments(MediaType.PDF, instanceId);

    if (enablePage) {
      this.fileTranscodeService.checkFileTranscoding(
        item,
        store.mediaTypeSignal() || MediaType.PDF,
        enablePage,
        filePages,
        instanceId
      );
    } else {
      store.updateStore({ mediaType: MediaType.PDF });
    }
  }

  private handleOfficeDocItem(
    item: MediaPlayerFile,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    this.handleTranscodableDocumentItem(item, MediaType.OFFICEDOC, enablePage, filePages, instanceId);
  }

  private handlePowerPointItem(
    item: MediaPlayerFile,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    this.handleTranscodableDocumentItem(item, MediaType.POWERPOINT, enablePage, filePages, instanceId);
  }

  /**
   * Handle transcodable document items (office docs, PowerPoint)
   */
  private handleTranscodableDocumentItem(
    item: MediaPlayerFile,
    mediaType: string,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    if (!instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    store.updateStore({ mediaType });
    // Update item and store with previewSRC
    if (item.signedURL) {
      item.previewSRC = item.signedURL + '?thumbnail=_PDF';
      store.updateStore({
        item: { ...item, previewSRC: item.previewSRC },
      });
    }
    this.fileTranscodeService.checkFileTranscoding(item, mediaType, enablePage, filePages, instanceId);

    if (store.fileTranscodingStatusSignal() === FileTranscodingStatus.SUCCESS) {
      this.annotationPluginService.initializeComments(MediaType.PDF, instanceId);
    }
  }

  private handleConvertibleImageItem(
    item: MediaPlayerFile,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    if (!instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const imgSrc: SafeStyle = this.safeResourceUrlService.toSafeStyle('url("' + item.signedURL + '?thumbnail=_IMG")');
    store.updateStore({ imgSrc });
    // Update store with previewSRC instead of mutating item directly
    if (item.signedURL && store.itemSignal()) {
      store.updateStore({
        item: { ...store.itemSignal(), previewSRC: item.signedURL + '?thumbnail=_IMG' },
      });
    }
    this.fileTranscodeService.checkFileTranscoding(item, MediaType.CONVERTIBLEIMAGE, enablePage, filePages, instanceId);
    this.annotationPluginService.initializeImageAnnotations(instanceId, true);
  }

  private handleKeynoteItem(
    item: MediaPlayerFile,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    if (!instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    store.updateStore({ mediaType: MediaType.KEYNOTE });
    this.fileTranscodeService.checkFileTranscoding(item, MediaType.KEYNOTE, enablePage, filePages, instanceId);

    if (store.fileTranscodingStatusSignal() === FileTranscodingStatus.SUCCESS) {
      // Update item and store with previewSRC
      if (item.signedURL) {
        item.previewSRC = item.signedURL + '?thumbnail=_PDF';
        store.updateStore({
          item: { ...item, previewSRC: item.previewSRC },
        });
      }
      this.annotationPluginService.initializeComments(MediaType.PDF, instanceId);
    }
  }

  private handleUnsupportedItem(item: MediaPlayerFile, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    item.publicLink = item.signedURL;

    this.annotationPluginService.initializeComments(MediaType.OTHER, instanceId);
    store.updateStore({ item: { ...item, publicLink: item.signedURL }, isFileNotSupported: true });
  }

  /**
   * Trigger a video viewer action from the service
   */
  public triggerVideoViewerAction(action: string, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;
    if (store.mediaTypeSignal() === MediaType.VIDEO) {
      store.updateStore({ videoViewerAction: action });
    }
  }

  /**
   * Trigger an audio viewer action from the service
   */
  public triggerAudioViewerAction(action: string, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;
    if (store.mediaTypeSignal() === MediaType.AUDIO) {
      store.updateStore({ audioViewerAction: action });
    }
  }

  /**
   * Dispose video player if it exists
   */
  private disposePlayerIfExist(instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const player: any = store.playerSignal();
    const isPlayerReady: boolean = store.isPlayerReadySignal();
    const mediaType: string | null = store.mediaTypeSignal();
    const item: MediaPlayerFile | null = store.itemSignal();

    if (player && isPlayerReady && mediaType === MediaType.VIDEO) {
      // Dispose video.js player
      if (typeof player.dispose === 'function') {
        player.dispose();
      }

      // Update state
      store.updateStore({
        player: null,
        isVideoPlayerLoaded: false,
        isPlayerReady: false,
        annotationPlugin: null,
      });

      // Emit disposal event
      onVideoPlayerDisposed.emit({
        act: 'videoPlayerDisposed',
        player: null,
        annotationPlugin: null,
        projectId: store.projectIdSignal(),
        sectionId: store.sectionIdSignal(),
        subSectionId: store.subsectionIdSignal(),
        typeId: store.typeIdSignal(),
        itemId: item?.id,
      });
    }
  }

  /**
   * Dispose all annotation plugins
   */
  private disposeAllPlugins(instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const annotationPlugin: any = store.annotationPluginSignal();

    if (annotationPlugin?.eventDispatcher?.pluginReady) {
      annotationPlugin.fire('dispose');
      store.updateStore({ annotationPlugin: null });
    }
  }

  /**
   * Cancel metadata check timeout
   */
  public cancelMetadataCheck(): void {
    if (this.metadataCheckTimeout) {
      clearTimeout(this.metadataCheckTimeout);
      this.metadataCheckTimeout = null;
    }
  }

  /**
   * Get download parameters for video download
   */
  public getDownloadParameters(quality: string | undefined, instanceId: string): string | null {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return null;

    const item: MediaPlayerFile | null = store.itemSignal();
    if (!item) {
      return null;
    }
    const params: string[] = [];
    if (item.sectionId) {
      params.push('sectionId=' + item.sectionId);
    }
    if (item.subSectionId) {
      params.push('subSectionId=' + item.subSectionId);
    }
    if (quality) {
      params.push('thumbnail=' + quality);
    }
    return params.length > 0 ? '?' + params.join('&') : null;
  }

  /**
   * Send download event to Google Analytics
   */
  public sendDownloadGAEvent(params: { url: string; quality?: string }, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const item: MediaPlayerFile | null = store.itemSignal();
    if (!item) {
      return;
    }

    try {
      const fileName: string = this.fileUtilsService.getFileDisplayName(item.displayName || item.name || '');
      const downloadEvent: Record<string, unknown> = {
        fileName,
        fileUrl: params.url,
        fileType: item.contentType,
        quality: params.quality,
        fileId: item.id,
        projectId: item.projectId,
        downloadTime: new Date().toISOString(),
        user: this.authService.getAuthUserName(),
        ...(item.sectionId && { sectionId: item.sectionId }),
        ...(item.subSectionId && { subSectionId: item.subSectionId }),
        ...(item.sourceId && { sourceId: item.sourceId }),
        ...(item.versionNumber && { versionNumber: item.versionNumber }),
      };
      this.gaService.sendCustomEvent('file_download', downloadEvent);
    } catch {
      // no-op
    }
  }
}
